// ============================================
// FILE: frontend/src/components/messaging/ChatWindow.jsx
// PURPOSE: Message input and display area for connection chat
// FIXED: Auto-polling now integrated, scroll works, messages update real-time
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Loader2, Send } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// MAIN COMPONENT
// ========================================

export function ChatWindow({
  connection,
  messages = [],
  loading = false,
  sending = false,
  onSendMessage,
  onLoadMore,
  onStartPolling,
  onStopPolling
}) {
  console.log('[ChatWindow] Rendering with connection:', {
    connectionId: connection?.id,
    messageCount: messages.length,
    loading
  });

  // ========================================
  // STATE
  // ========================================

  const [messageInput, setMessageInput] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollingStartedRef = useRef(false);

  // ========================================
  // EFFECTS
  // ========================================

  // Auto-scroll to bottom ONLY when user sends a message
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  
  useEffect(() => {
    if (shouldAutoScroll) {
      console.log('[ChatWindow] Auto-scrolling to bottom after send');
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setShouldAutoScroll(false); // Reset flag
      }, 100);
    }
  }, [messages, shouldAutoScroll]);

  // Start polling when component mounts
  useEffect(() => {
    if (!connection?.id || pollingStartedRef.current) {
      console.log('[ChatWindow] Skipping polling start (already started or no connection)');
      return;
    }

    console.log('[ChatWindow] 🔄 Starting message polling on mount');
    pollingStartedRef.current = true;
    
    if (onStartPolling) {
      onStartPolling(connection.id, 90000); // Poll every 90 seconds
    }

    return () => {
      console.log('[ChatWindow] 🛑 Stopping polling on unmount');
      pollingStartedRef.current = false;
      if (onStopPolling) {
        onStopPolling();
      }
    };
  }, [connection?.id, onStartPolling, onStopPolling]);

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const handleInputChange = (e) => {
    const value = e.target.value;
    console.log('[ChatWindow] Input changed, length:', value.length);
    setMessageInput(value);
    setCharacterCount(value.length);
  };

  const handleSendMessage = async () => {
    console.log('[ChatWindow] Send button clicked');

    if (!messageInput.trim()) {
      console.log('[ChatWindow] Empty message, ignoring');
      return;
    }

    if (messageInput.length > 5000) {
      console.error('[ChatWindow] Message too long:', messageInput.length);
      return;
    }

    try {
      console.log('[ChatWindow] Sending message...');
      await onSendMessage(messageInput);
      setMessageInput('');
      setCharacterCount(0);
      setShouldAutoScroll(true); // ⭐ ONLY scroll after sending
      console.log('[ChatWindow] ✅ Message sent successfully');
    } catch (error) {
      console.error('[ChatWindow] ❌ Send error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      console.log('[ChatWindow] Enter pressed, sending message');
      handleSendMessage();
    }
  };

  const handleLoadMore = () => {
    console.log('[ChatWindow] Load more clicked');
    if (onLoadMore) {
      onLoadMore();
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================

  if (loading && messages.length === 0) {
    console.log('[ChatWindow] Loading initial messages');
    return (
      <Card className="h-full flex flex-col">
        <CardContent className="flex items-center justify-center flex-1">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading messages...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // EMPTY STATE
  // ========================================

  if (!messages || messages.length === 0) {
    console.log('[ChatWindow] Empty messages - showing input field');
    return (
      <Card className="h-full flex flex-col">
        {/* Header */}
        <CardHeader className="border-b pb-3">
          <CardTitle className="text-lg">
            Chat with {connection?.otherParty?.fullName || 'User'}
          </CardTitle>
        </CardHeader>

        {/* Empty state message */}
        <CardContent className="flex items-center justify-center flex-1">
          <p className="text-sm text-muted-foreground">
            No messages yet. Start the conversation!
          </p>
        </CardContent>

        {/* MESSAGE INPUT - Same as main render */}
        <CardFooter className="border-t p-4 flex gap-2 flex-shrink-0">
          <div className="flex-1 flex flex-col gap-2">
            <textarea
              value={messageInput}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Shift+Enter for new line)"
              className="flex-1 px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-10 max-h-24 text-sm"
              disabled={sending}
              maxLength={5000}
            />

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {characterCount} / 5000
              </span>

              <Button
                onClick={handleSendMessage}
                disabled={sending || !messageInput.trim()}
                size="sm"
                className="gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
    );
  }

  // ========================================
  // RENDER
  // ========================================

  console.log('[ChatWindow] Rendering messages:', messages.length);

// ⭐ TEMPORARY DEBUG - Check what we're rendering
  console.log('[ChatWindow] 🔍 DEBUG:', {
    hasConnection: !!connection,
    connectionId: connection?.id,
    messageCount: messages.length,
    loading,
    sending,
    hasOnSendMessage: !!onSendMessage,
    isEmpty: !messages || messages.length === 0
  });

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      {/* ========================================
          HEADER
          ======================================== */}
      <CardHeader className="border-b pb-3">
        <CardTitle className="text-lg">
          Chat with {connection?.otherParty?.fullName || 'User'}
        </CardTitle>
      </CardHeader>

      {/* ========================================
          MESSAGES AREA
          ======================================== */}
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Load more button */}
        {messages.length > 10 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={loading}
            className="w-full mb-4"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load Earlier Messages'
            )}
          </Button>
        )}

        {/* Messages */}
        {messages.map((message, index) => (
          <div
            key={message.id || index}
            className={`flex gap-3 ${
              message.senderId === connection.currentUserId
                ? 'flex-row-reverse'
                : 'flex-row'
            }`}
          >
            {/* Avatar */}
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarImage
                src={message.sender?.photoURL}
                alt={message.sender?.fullName}
              />
              <AvatarFallback>
                {message.sender?.fullName?.[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>

            {/* Message bubble */}
            <div
              className={`flex flex-col gap-1 max-w-xs ${
                message.senderId === connection.currentUserId
                  ? 'items-end'
                  : 'items-start'
              }`}
            >
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-muted-foreground">
                  {message.sender?.fullName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(message.createdAt), 'p')}
                </p>
              </div>

              <div
                className={`px-4 py-2 rounded-lg break-words ${
                  message.senderId === connection.currentUserId
                    ? 'bg-primary text-primary-foreground rounded-br-none'
                    : 'bg-muted text-muted-foreground rounded-bl-none'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">
                  {message.content}
                </p>
              </div>

              {/* Read status */}
              {message.isRead && message.senderId === connection.currentUserId && (
                <p className="text-xs text-muted-foreground">
                  Read {format(new Date(message.readAt), 'p')}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </CardContent>

      {/* ========================================
          MESSAGE INPUT
          ======================================== */}
      <CardFooter className="border-t p-4 flex gap-2 flex-shrink-0">
        <div className="flex-1 flex flex-col gap-2">
          <textarea
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type your message... (Shift+Enter for new line)"
            className="flex-1 px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-10 max-h-24 text-sm"
            disabled={sending}
            maxLength={5000}
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {characterCount} / 5000
            </span>

            <Button
              onClick={handleSendMessage}
              disabled={sending || !messageInput.trim()}
              size="sm"
              className="gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send
                </>
              )}
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}

export default ChatWindow;